import { useCallback, useEffect, useMemo, useState } from "react";

// ─── StepOne.tsx ───────────────────────────────────────────────
import { useTranslation } from "react-i18next";

// React Hook Form helpers
import { useFormContext } from "react-hook-form";

// Common UI Components
import PrimaryButton from "../../common/PrimaryButton";
import TextInput from "../../hook_forms/hook_textInput";
import ImageUpload from "../../forms/imageUpload";
import TextArea from "../../hook_forms/hook_textarea";

// Styles
import styles from "./AddOrganisation.module.scss";

// ─── Zustand Store ───────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";
import OutlinedButton from "../../common/OutlinedButton";
import Spinner from "../../common/Spinner";
import { toast, ToastContainer } from "react-toastify";

// ─── API Services ───────────────────────────────────────────
import { editOrganization } from "../../../setup/api/services/organization";
import { uploadFile } from "../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../s3Integration/enums";
import { getUserId } from "../../../setup/auth/oidc";

// ─── StepOne Component ─────────────────────────────────────
// This step handles uploading logo and entering basic organization details.
const StepOne = ({
  onNext,
  onCancel,
}: {
  onNext: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();

  // local state
  const [images, setImages] = useState<File | string>();
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchOrganizations = useOrganisationStore(
    (state) => state.fetchOrganizations
  );
  const setSelectedOrganization = useOrganisationStore(
    (state) => state.setSelectedOrganization
  );
  const selectedOrganisation = useOrganisationStore(
    (state) => state.selectedOrganisation
  );

  useEffect(() => {
    if (selectedOrganisation?.logoUrl) {
      setImages(selectedOrganisation?.logoUrl);
    }
  }, [selectedOrganisation?.logoUrl]);

  const {
    register,
    setValue,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useFormContext();

  const userId = getUserId();

  const name = watch("name");
  const code = watch("code");
  const websiteUrl = watch("websiteUrl");
  const description = watch("description");

  useEffect(() => {
    if (selectedOrganisation) {
      reset({
        name: selectedOrganisation.name || "",
        code: selectedOrganisation.code || "",
        websiteUrl: selectedOrganisation.websiteUrl || "",
        description: selectedOrganisation.description || "",
        logo: selectedOrganisation.logoUrl
          ? [selectedOrganisation.logoUrl]
          : [],
      });
    } else {
      reset({
        name: "",
        code: "",
        websiteUrl: "",
        description: "",
        logo: [],
      });
    }

    setIsPrefilled(true);
  }, [reset, selectedOrganisation, setValue]);

  const uploadImage = useCallback(
    (logo: File): Promise<string> => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          if (logo && userId) {
            const url = await uploadFile(
              logo,
              EUploadImageType.PROFILE,
              userId
            );
            resolve(url);
          } else {
            reject(new Error("Logo file is null"));
          }
        } catch (error) {
          reject(error);
        }
      });
    },
    [userId]
  );

  const handleNextClick = useCallback(async () => {
    if (!selectedOrganisation) {
      onNext();
      return;
    }

    const formValues = getValues();
    const { name, code, logo, websiteUrl, description } = formValues;

    setLoading(true);
    let logoUrl = "";

    if (typeof logo !== "string") {
      if (logo && logo.length > 0 && logo[0]) {
        try {
          logoUrl = await uploadImage(logo[0]);
        } catch (error) {
          logoUrl = "";
        }
      } else {
        logoUrl = "";
      }
    } else {
      logoUrl = logo;
    }

    const payload = {
      name,
      code,
      logoUrl: logoUrl || "",
      websiteUrl,
      description,
      mappedStatus: true,
      manualWithdrawEnabled: true,
    };

    try {
      await editOrganization(selectedOrganisation.code, payload);
      toast(t("organization_saved_successfully"));
      fetchOrganizations();
      onNext();
    } catch (error) {
      console.error(error);
      toast(t("organization_save_error"));
    } finally {
      setLoading(false);
      setSelectedOrganization();
    }
  }, [
    getValues,
    onNext,
    selectedOrganisation,
    setSelectedOrganization,
    t,
    uploadImage,
    fetchOrganizations,
  ]);

  // handling image upload
  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const image = e?.target?.files?.[0];
      if (image) {
        setValue("logo", [image]);
        setImages(image);
      } else {
        setValue("logo", undefined); // <-- Important if no file selected
        setImages(undefined);
      }
    },
    [setValue]
  );

  const handleDeleteImage = useCallback(() => {
    setValue("logo", undefined);
    setImages(undefined);
  }, [setValue]);

  const isNextDisabled = useMemo(() => {
    return (
      !name ||
      name?.length < 2 ||
      !code ||
      code?.length < 2 ||
      !websiteUrl ||
      !!errors.websiteUrl ||
      description?.length > 500
    );
  }, [code, description?.length, errors.websiteUrl, name, websiteUrl]);

  if (!isPrefilled) return null; // or show a spinner

  return (
    <div className={styles["step_container"]}>
      {/* Name */}

      <TextInput
        {...register("name")}
        style={{ flex: 1 }}
        placeholder={t("organisation_name")}
        label={t("organisation_name")}
        errorMessage={errors.name?.message?.toString()}
      />

      {/* Website URL */}

      <TextInput
        {...register("websiteUrl")}
        style={{ flex: 1 }}
        placeholder={t("website_link")}
        label={t("website_link")}
        errorMessage={errors.websiteUrl?.message?.toString()}
      />

      {/* Code */}

      <TextInput
        {...register("code")}
        style={{ flex: 1 }}
        placeholder={t("code")}
        label={t("code")}
        errorMessage={errors.code?.message?.toString()}
      />

      {/* Description */}

      <TextArea
        {...register("description")}
        placeholder={t("add_organisation_description")}
        label={t("add_organisation_description")}
        maxCharacters={500}
        errorMessage={errors.description?.message?.toString()}
      />

      <p className={styles["title"]}>{t("organisation_logo")}</p>
      {!images ? (
        <ImageUpload handleChange={handleImageUpload} />
      ) : (
        <div className={styles["uploaded_image_container"]}>
          <img
            className={styles["uploaded_image"]}
            src={
              typeof images === "string" ? images : URL.createObjectURL(images)
            }
            alt="logo"
          />
          <div onClick={handleDeleteImage} className={styles["delete_icon"]}>
            <img
              src="/assets/images/svg/delete_logo.svg"
              className={styles["icon"]}
            />
          </div>
        </div>
      )}
      <div className={styles["button_container"]}>
        <OutlinedButton onClick={onCancel} style={{ flex: 1 }}>
          {t("lbl_cancel")}
        </OutlinedButton>
        <PrimaryButton
          color="primary"
          onClick={handleNextClick}
          style={{ flex: 3 }}
          disabled={isNextDisabled || loading}>
          {loading ? (
            <Spinner />
          ) : selectedOrganisation ? (
            t("lbl_edit_profile_save_btn")
          ) : (
            t("next")
          )}
        </PrimaryButton>
      </div>
      <ToastContainer />
    </div>
  );
};

export default StepOne;
